import type {
  CelebrationVoiceId,
  MusicGenreId,
  SoundPackId,
} from "../types";

export const SOUND_PACK_OPTIONS: ReadonlyArray<{
  id: SoundPackId;
  label: string;
  description: string;
  character: string;
}> = [
  {
    id: "cinematic",
    label: "Cinematic",
    description: "Heroic orchestra, rising brass, precision lock, and a blockbuster reveal.",
    character: "Heroic",
  },
  {
    id: "party",
    label: "Party",
    description: "Festival synths, confetti hits, applause, and crowd energy.",
    character: "Big energy",
  },
  {
    id: "rock",
    label: "Rock",
    description: "Power chords, drums, cymbal crash, and arena celebration.",
    character: "Arena",
  },
  {
    id: "arcade",
    label: "Arcade",
    description: "Retro countdown, coin sparkle, and victory fanfare.",
    character: "Playful",
  },
];

let audioContext: AudioContext | null = null;
let masterInput: GainNode | null = null;
let musicBus: GainNode | null = null;
let voiceBus: GainNode | null = null;
let activeMusicSource: AudioBufferSourceNode | null = null;
let activeMusicGain: GainNode | null = null;
let activeMusicLevel = 0;
let musicRequestId = 0;
let celebrationRequestId = 0;
const activeCelebrationSources = new Set<AudioBufferSourceNode>();
const bufferCache = new Map<string, Promise<AudioBuffer>>();
const encodedAudioCache = new Map<string, Promise<ArrayBuffer>>();

function getContext(): AudioContext | null {
  if (typeof window === "undefined" || !("AudioContext" in window)) return null;
  try {
    audioContext ??= new AudioContext();
    if (!masterInput) {
      masterInput = audioContext.createGain();
      masterInput.gain.value = 0.72;
      const compressor = audioContext.createDynamicsCompressor();
      compressor.threshold.value = -18;
      compressor.knee.value = 18;
      compressor.ratio.value = 8;
      compressor.attack.value = 0.004;
      compressor.release.value = 0.22;
      masterInput.connect(compressor);
      compressor.connect(audioContext.destination);
    }
    if (!musicBus) {
      musicBus = audioContext.createGain();
      musicBus.gain.value = 1;
      musicBus.connect(masterInput);
    }
    if (!voiceBus) {
      voiceBus = audioContext.createGain();
      voiceBus.gain.value = 0.94;
      voiceBus.connect(masterInput);
    }
    if (audioContext.state === "suspended") void audioContext.resume();
    return audioContext;
  } catch {
    audioContext = null;
    masterInput = null;
    musicBus = null;
    voiceBus = null;
    return null;
  }
}

function output(): GainNode | null {
  getContext();
  return masterInput;
}

function scheduleTone(options: {
  frequency: number;
  endFrequency?: number;
  duration: number;
  gain: number;
  delay?: number;
  attack?: number;
  type?: OscillatorType;
  detune?: number;
}): void {
  const context = getContext();
  const destination = output();
  if (!context || !destination) return;
  const delay = options.delay ?? 0;
  const startsAt = context.currentTime + Math.max(0.005, delay);
  const endsAt = startsAt + options.duration;
  const attack = Math.min(options.attack ?? 0.018, options.duration * 0.42);
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = options.type ?? "sine";
  oscillator.frequency.setValueAtTime(options.frequency, startsAt);
  if (options.endFrequency) {
    oscillator.frequency.exponentialRampToValueAtTime(options.endFrequency, endsAt);
  }
  oscillator.detune.value = options.detune ?? 0;
  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(options.gain, startsAt + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, endsAt);
  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start(startsAt);
  oscillator.stop(endsAt + 0.03);
}

function deterministicNoise(length: number, seed: number): Float32Array {
  const values = new Float32Array(length);
  let state = seed >>> 0;
  for (let index = 0; index < length; index += 1) {
    state = (Math.imul(state, 1_664_525) + 1_013_904_223) >>> 0;
    values[index] = (state / 4_294_967_296) * 2 - 1;
  }
  return values;
}

function scheduleNoise(options: {
  duration: number;
  gain: number;
  delay?: number;
  frequency?: number;
  filterType?: BiquadFilterType;
  attack?: number;
  seed?: number;
}): void {
  const context = getContext();
  const destination = output();
  if (!context || !destination) return;
  const startsAt = context.currentTime + Math.max(0.005, options.delay ?? 0);
  const endsAt = startsAt + options.duration;
  const frameCount = Math.max(1, Math.floor(context.sampleRate * options.duration));
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  buffer.getChannelData(0).set(deterministicNoise(frameCount, options.seed ?? 7_919));
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  source.buffer = buffer;
  filter.type = options.filterType ?? "bandpass";
  filter.frequency.value = options.frequency ?? 1_800;
  filter.Q.value = filter.type === "bandpass" ? 0.9 : 0.45;
  const attack = Math.min(options.attack ?? 0.008, options.duration * 0.35);
  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(options.gain, startsAt + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, endsAt);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(destination);
  source.start(startsAt);
  source.stop(endsAt + 0.02);
}

function kick(delay = 0, gain = 0.12): void {
  scheduleTone({
    frequency: 132,
    endFrequency: 46,
    duration: 0.24,
    gain,
    delay,
    attack: 0.005,
    type: "sine",
  });
  scheduleNoise({
    duration: 0.035,
    gain: gain * 0.45,
    delay,
    frequency: 2_400,
    filterType: "highpass",
    seed: 401 + Math.round(delay * 1_000),
  });
}

function clap(delay = 0, gain = 0.055, seed = 17): void {
  [0, 0.018, 0.036].forEach((offset, index) =>
    scheduleNoise({
      duration: 0.07,
      gain: gain * (1 - index * 0.18),
      delay: delay + offset,
      frequency: 1_600 + index * 460,
      filterType: "bandpass",
      seed: seed + index * 31,
    }),
  );
}

function applause(delay = 0, intensity = 1): void {
  const count = Math.round(15 + intensity * 11);
  for (let index = 0; index < count; index += 1) {
    const spread = ((index * 47) % 101) / 100;
    const jitter = ((index * 29) % 17) / 1_000;
    clap(
      delay + 0.08 + spread * (1.35 + intensity * 0.42) + jitter,
      0.012 + intensity * 0.012,
      2_003 + index * 73,
    );
  }
}

function whoop(delay = 0, pitch = 1): void {
  scheduleTone({
    frequency: 310 * pitch,
    endFrequency: 720 * pitch,
    duration: 0.42,
    gain: 0.022,
    delay,
    attack: 0.055,
    type: "sine",
  });
  scheduleTone({
    frequency: 420 * pitch,
    endFrequency: 890 * pitch,
    duration: 0.34,
    gain: 0.012,
    delay: delay + 0.08,
    attack: 0.04,
    type: "triangle",
  });
}

function shimmer(delay = 0, intensity = 1): void {
  [1_046.5, 1_318.5, 1_568, 2_093].forEach((frequency, index) =>
    scheduleTone({
      frequency,
      endFrequency: frequency * 1.16,
      duration: 0.28 + index * 0.07,
      gain: 0.009 * intensity,
      delay: delay + index * 0.055,
      attack: 0.008,
      type: "sine",
      detune: index % 2 === 0 ? -4 : 5,
    }),
  );
}

function powerChord(
  root: number,
  delay = 0,
  duration = 0.75,
  gain = 0.038,
): void {
  const context = getContext();
  const destination = output();
  if (!context || !destination) return;
  const startsAt = context.currentTime + Math.max(0.005, delay);
  const endsAt = startsAt + duration;
  const waveShaper = context.createWaveShaper();
  const curve = new Float32Array(512);
  for (let index = 0; index < curve.length; index += 1) {
    const x = (index * 2) / (curve.length - 1) - 1;
    curve[index] = Math.tanh(x * 3.4);
  }
  waveShaper.curve = curve;
  waveShaper.oversample = "2x";
  const filter = context.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(2_700, startsAt);
  filter.frequency.exponentialRampToValueAtTime(980, endsAt);
  const chordGain = context.createGain();
  chordGain.gain.setValueAtTime(0.0001, startsAt);
  chordGain.gain.exponentialRampToValueAtTime(gain, startsAt + 0.015);
  chordGain.gain.exponentialRampToValueAtTime(0.0001, endsAt);
  waveShaper.connect(filter);
  filter.connect(chordGain);
  chordGain.connect(destination);

  [root, root * 1.5, root * 2].forEach((frequency, index) => {
    const oscillator = context.createOscillator();
    oscillator.type = index === 0 ? "sawtooth" : "square";
    oscillator.frequency.value = frequency;
    oscillator.detune.value = index * 3 - 3;
    oscillator.connect(waveShaper);
    oscillator.start(startsAt);
    oscillator.stop(endsAt + 0.03);
  });
}

function cymbal(delay = 0, gain = 0.055): void {
  scheduleNoise({
    duration: 1.35,
    gain,
    delay,
    frequency: 4_800,
    filterType: "highpass",
    attack: 0.003,
    seed: 91_337,
  });
  shimmer(delay + 0.04, 0.55);
}

function playCinematicLaunch(): void {
  scheduleTone({
    frequency: 58,
    endFrequency: 116,
    duration: 0.58,
    gain: 0.075,
    type: "sine",
  });
  scheduleTone({
    frequency: 174,
    endFrequency: 261.63,
    duration: 0.52,
    gain: 0.028,
    delay: 0.07,
    type: "sawtooth",
  });
  scheduleNoise({
    duration: 0.48,
    gain: 0.018,
    delay: 0.04,
    frequency: 1_200,
    filterType: "highpass",
    seed: 4_011,
  });
}

function playPartyLaunch(): void {
  kick(0, 0.13);
  kick(0.23, 0.09);
  [261.63, 329.63, 392, 523.25].forEach((frequency, index) =>
    scheduleTone({
      frequency,
      duration: 0.18,
      gain: 0.027,
      delay: 0.02 + index * 0.075,
      type: "sawtooth",
    }),
  );
  clap(0.35, 0.042, 517);
}

function playRockLaunch(): void {
  kick(0, 0.14);
  powerChord(82.41, 0.02, 0.54, 0.047);
  powerChord(110, 0.28, 0.48, 0.041);
}

function playArcadeLaunch(): void {
  [196, 246.94, 293.66, 392, 493.88].forEach((frequency, index) =>
    scheduleTone({
      frequency,
      duration: 0.12,
      gain: 0.025,
      delay: index * 0.065,
      type: "square",
    }),
  );
}

function playCinematicStop(): void {
  kick(0, 0.085);
  scheduleTone({
    frequency: 220,
    endFrequency: 440,
    duration: 0.46,
    gain: 0.025,
    type: "triangle",
  });
  scheduleTone({
    frequency: 330,
    endFrequency: 660,
    duration: 0.42,
    gain: 0.018,
    delay: 0.08,
    type: "sine",
  });
}

function playPartyStop(): void {
  [0, 0.08, 0.15, 0.21].forEach((delay, index) =>
    clap(delay, 0.025 + index * 0.007, 703 + index * 17),
  );
  scheduleTone({
    frequency: 360,
    endFrequency: 920,
    duration: 0.48,
    gain: 0.024,
    delay: 0.03,
    type: "sawtooth",
  });
}

function playRockStop(): void {
  kick(0, 0.12);
  clap(0.12, 0.052, 911);
  kick(0.25, 0.11);
  scheduleTone({
    frequency: 96,
    endFrequency: 72,
    duration: 0.5,
    gain: 0.045,
    delay: 0.18,
    type: "sawtooth",
  });
}

function playArcadeStop(): void {
  [659.25, 587.33, 523.25, 440, 659.25].forEach((frequency, index) =>
    scheduleTone({
      frequency,
      duration: 0.1,
      gain: 0.023,
      delay: index * 0.075,
      type: "square",
    }),
  );
}

function playCinematicReveal(): void {
  kick(0, 0.11);
  [261.63, 392, 523.25, 659.25].forEach((frequency, index) =>
    scheduleTone({
      frequency,
      duration: 0.85 + index * 0.07,
      gain: 0.029 - index * 0.003,
      delay: index * 0.055,
      type: index < 2 ? "triangle" : "sine",
    }),
  );
  shimmer(0.2, 0.9);
  applause(0.28, 0.42);
}

function playPartyReveal(): void {
  kick(0, 0.14);
  clap(0.12, 0.065, 1_203);
  [523.25, 659.25, 783.99, 1_046.5].forEach((frequency, index) =>
    scheduleTone({
      frequency,
      duration: 0.5 + index * 0.08,
      gain: 0.034 - index * 0.004,
      delay: 0.03 + index * 0.065,
      type: "sawtooth",
    }),
  );
  shimmer(0.18, 1.2);
  whoop(0.25, 0.92);
  whoop(0.48, 1.08);
  applause(0.12, 1);
}

function playRockReveal(): void {
  kick(0, 0.15);
  powerChord(98, 0.01, 1.05, 0.055);
  powerChord(130.81, 0.24, 0.88, 0.045);
  cymbal(0.04, 0.062);
  clap(0.3, 0.05, 1_607);
  whoop(0.38, 0.78);
  applause(0.22, 0.78);
}

function playArcadeReveal(): void {
  [523.25, 659.25, 783.99, 1_046.5, 1_318.5].forEach((frequency, index) =>
    scheduleTone({
      frequency,
      duration: index === 4 ? 0.58 : 0.14,
      gain: index === 4 ? 0.035 : 0.027,
      delay: index * 0.075,
      type: index === 4 ? "sine" : "square",
    }),
  );
  shimmer(0.24, 1.05);
  applause(0.35, 0.32);
}

const musicFiles: Record<SoundPackId, string> = {
  cinematic: "music-cinematic-heroic.ogg",
  party: "music-party.ogg",
  rock: "music-rock.ogg",
  arcade: "music-arcade.mp3",
};

const musicStartOffsets: Record<SoundPackId, number> = {
  cinematic: 1.52,
  party: 0,
  rock: 0,
  arcade: 0,
};

function audioAssetUrl(fileName: string): string {
  return new URL(`audio/${fileName}`, document.baseURI).toString();
}

function loadEncodedAudio(fileName: string): Promise<ArrayBuffer> {
  const cached = encodedAudioCache.get(fileName);
  if (cached) return cached;
  const request = fetch(audioAssetUrl(fileName))
    .then((response) => {
      if (!response.ok) throw new Error(`Audio asset failed: ${fileName}`);
      return response.arrayBuffer();
    })
    .catch((error: unknown) => {
      encodedAudioCache.delete(fileName);
      throw error;
    });
  encodedAudioCache.set(fileName, request);
  return request;
}

function decodeAndCacheAudio(
  fileName: string,
  context: BaseAudioContext,
): Promise<AudioBuffer> {
  const request = loadEncodedAudio(fileName)
    .then((data) => context.decodeAudioData(data.slice(0)))
    .catch((error: unknown) => {
      bufferCache.delete(fileName);
      throw error;
    });
  bufferCache.set(fileName, request);
  return request;
}

function loadAudioBuffer(fileName: string): Promise<AudioBuffer> {
  const context = getContext();
  if (!context) return Promise.reject(new Error("Audio is unavailable."));
  return bufferCache.get(fileName) ?? decodeAndCacheAudio(fileName, context);
}

function preloadAudioBuffer(fileName: string): void {
  if (bufferCache.has(fileName)) return;
  if (typeof window === "undefined" || !("OfflineAudioContext" in window)) {
    void loadEncodedAudio(fileName).catch(() => {
      // The regular audio context can retry on the first user gesture.
    });
    return;
  }
  try {
    const decoder = new window.OfflineAudioContext(1, 1, 44_100);
    void decodeAndCacheAudio(fileName, decoder).catch(() => {
      // The regular audio context can retry on the first user gesture.
    });
  } catch {
    void loadEncodedAudio(fileName).catch(() => {
      // The regular audio context can retry on the first user gesture.
    });
  }
}

function fadeCurrentMusic(fadeSeconds: number): void {
  const context = getContext();
  if (!context || !activeMusicSource || !activeMusicGain) return;
  const source = activeMusicSource;
  const gain = activeMusicGain;
  const now = context.currentTime;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + fadeSeconds);
  try {
    source.stop(now + fadeSeconds + 0.05);
  } catch {
    // The source may already be stopping.
  }
  if (source === activeMusicSource) {
    activeMusicSource = null;
    activeMusicGain = null;
    activeMusicLevel = 0;
  }
}

function resolvedMusicGenre(pack: SoundPackId, genre: MusicGenreId): SoundPackId | null {
  if (genre === "off") return null;
  return genre === "follow-pack" ? pack : genre;
}

async function startGenreMusic(
  pack: SoundPackId,
  genre: MusicGenreId,
  volume: number,
): Promise<void> {
  const context = getContext();
  if (!context || !musicBus) return;
  const resolved = resolvedMusicGenre(pack, genre);
  const requestId = ++musicRequestId;
  fadeCurrentMusic(0.28);
  if (!resolved || volume <= 0) return;
  try {
    const buffer = await loadAudioBuffer(musicFiles[resolved]);
    if (requestId !== musicRequestId) return;
    const source = context.createBufferSource();
    const gain = context.createGain();
    const normalizedVolume = Math.min(1, Math.max(0, volume / 100));
    const level = 0.46 * Math.pow(normalizedVolume, 1.35);
    const now = context.currentTime;
    const startOffset = musicStartOffsets[resolved];
    source.buffer = buffer;
    source.loop = true;
    source.loopStart = startOffset;
    if (resolved === "cinematic") {
      gain.gain.setValueAtTime(Math.max(0.0001, level), now);
    } else {
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(
        Math.max(0.0001, level),
        now + 0.52,
      );
    }
    source.connect(gain);
    gain.connect(musicBus);
    source.start(now, startOffset);
    activeMusicSource = source;
    activeMusicGain = gain;
    activeMusicLevel = level;
    source.onended = () => {
      if (activeMusicSource === source) {
        activeMusicSource = null;
        activeMusicGain = null;
        activeMusicLevel = 0;
      }
    };
  } catch {
    // Procedural cues remain available when an asset cannot be decoded.
  }
}

function duckAndFadeMusic(): void {
  const context = getContext();
  musicRequestId += 1;
  if (!context || !activeMusicSource || !activeMusicGain) return;
  const source = activeMusicSource;
  const gain = activeMusicGain;
  const now = context.currentTime;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value || activeMusicLevel), now);
  gain.gain.exponentialRampToValueAtTime(
    Math.max(0.0001, activeMusicLevel * 0.18),
    now + 0.13,
  );
  gain.gain.setValueAtTime(Math.max(0.0001, activeMusicLevel * 0.18), now + 1.7);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 3.15);
  try {
    source.stop(now + 3.22);
  } catch {
    // The source may already be stopping.
  }
  activeMusicSource = null;
  activeMusicGain = null;
  activeMusicLevel = 0;
}

function cancelRecordedCelebration(): void {
  celebrationRequestId += 1;
  activeCelebrationSources.forEach((source) => {
    try {
      source.stop();
    } catch {
      // The source may already have ended.
    }
  });
  activeCelebrationSources.clear();
}

function scheduleBuffer(
  buffer: AudioBuffer,
  destination: AudioNode,
  delay: number,
  level: number,
  durationSeconds?: number,
): AudioBufferSourceNode | null {
  const context = getContext();
  if (!context) return null;
  const source = context.createBufferSource();
  const gain = context.createGain();
  const startsAt = context.currentTime + delay;
  source.buffer = buffer;
  gain.gain.value = level;
  source.connect(gain);
  gain.connect(destination);
  activeCelebrationSources.add(source);
  source.onended = () => activeCelebrationSources.delete(source);
  source.start(startsAt);
  if (durationSeconds) {
    const playSeconds = Math.min(durationSeconds, buffer.duration);
    if (playSeconds > 0.5) {
      gain.gain.setValueAtTime(level, startsAt + playSeconds - 0.45);
      gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + playSeconds);
    }
    source.stop(startsAt + playSeconds + 0.02);
  }
  return source;
}

function celebrationFiles(pack: SoundPackId): string[] {
  const phrase = pack === "arcade" ? "you-win" : "congratulations";
  return [
    `voice-female-${phrase}.ogg`,
    `voice-male-${phrase}.ogg`,
    "crowd-applause.ogg",
    "crowd-cheers.ogg",
    pack === "cinematic" ? "cinematic-reveal.ogg" : "victory.wav",
  ];
}

function prewarmCelebration(pack: SoundPackId): void {
  void Promise.all(celebrationFiles(pack).map((file) => loadAudioBuffer(file))).catch(
    () => {
      // Procedural cues remain available if an optional recording cannot load.
    },
  );
}

async function playRecordedCelebration(
  pack: SoundPackId,
  voice: CelebrationVoiceId,
): Promise<void> {
  const context = getContext();
  const destination = voiceBus;
  if (!context || !destination) return;
  const requestId = ++celebrationRequestId;
  const normalizedVoice =
    voice === "woohoo" || voice === "congratulations" ? "auto" : voice;
  const selectedGender =
    normalizedVoice === "male" || normalizedVoice === "female"
      ? normalizedVoice
      : pack === "rock" || pack === "arcade"
        ? "male"
        : "female";
  const phrase = pack === "arcade" ? "you-win" : "congratulations";
  const voiceFile = `voice-${selectedGender}-${phrase}.ogg`;
  const wantsVoice = normalizedVoice !== "crowd" && normalizedVoice !== "off";
  const isCinematic = pack === "cinematic";
  const revealFile = isCinematic ? "cinematic-reveal.ogg" : "victory.wav";
  const files = wantsVoice
    ? [voiceFile, "crowd-applause.ogg", "crowd-cheers.ogg", revealFile]
    : ["crowd-applause.ogg", "crowd-cheers.ogg", revealFile];
  try {
    const buffers = await Promise.all(files.map((file) => loadAudioBuffer(file)));
    if (requestId !== celebrationRequestId) return;
    if (wantsVoice) {
      scheduleBuffer(buffers[0]!, destination, isCinematic ? 0.68 : 0.28, 0.9);
      scheduleBuffer(buffers[1]!, destination, 0.12, 0.66, 5.2);
      scheduleBuffer(buffers[2]!, destination, isCinematic ? 0.54 : 0.42, 0.64);
      scheduleBuffer(buffers[3]!, destination, 0.02, isCinematic ? 0.58 : 0.46);
    } else {
      scheduleBuffer(buffers[0]!, destination, 0.08, 0.82, 5.2);
      scheduleBuffer(buffers[1]!, destination, isCinematic ? 0.5 : 0.36, 0.72);
      scheduleBuffer(buffers[2]!, destination, 0.02, isCinematic ? 0.58 : 0.46);
    }
  } catch {
    // Procedural winner cues still play if a recorded asset cannot load.
  }
}

export function preloadLaunchMusic(
  pack: SoundPackId = "cinematic",
  musicGenre: MusicGenreId = "follow-pack",
): void {
  const resolved = resolvedMusicGenre(pack, musicGenre);
  if (!resolved) return;
  preloadAudioBuffer(musicFiles[resolved]);
}

export function playLaunchCue(
  pack: SoundPackId = "cinematic",
  musicGenre: MusicGenreId = "follow-pack",
  musicVolume = 58,
): void {
  cancelRecordedCelebration();
  void startGenreMusic(pack, musicGenre, musicVolume);
  prewarmCelebration(pack);
  if (pack === "party") playPartyLaunch();
  else if (pack === "rock") playRockLaunch();
  else if (pack === "arcade") playArcadeLaunch();
  else playCinematicLaunch();
}

export function playStopCue(pack: SoundPackId = "cinematic"): void {
  if (pack === "party") playPartyStop();
  else if (pack === "rock") playRockStop();
  else if (pack === "arcade") playArcadeStop();
  else playCinematicStop();
}

export function playRevealCue(
  pack: SoundPackId = "cinematic",
  voice: CelebrationVoiceId = "auto",
): void {
  duckAndFadeMusic();
  if (pack === "party") playPartyReveal();
  else if (pack === "rock") playRockReveal();
  else if (pack === "arcade") playArcadeReveal();
  else playCinematicReveal();
  void playRecordedCelebration(pack, voice);
}
